import { useState } from "react";
import { Gift, Clock3, CalendarHeart, Info, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Reveal from "@/components/site/Reveal";

const plans = [
  {
    icon: Gift,
    name: "Erstgespräch",
    duration: "30 Minuten",
    price: "Kostenlos",
    note: "Unverbindlich kennenlernen – wir schauen gemeinsam, ob und wie ich dich unterstützen kann.",
    highlight: false,
    lookupKey: null,
  },
  {
    icon: Clock3,
    name: "Einzelberatung",
    duration: "60 Minuten",
    price: "70 €",
    note: "Eine volle Stunde nur für dich – online oder nach Vereinbarung persönlich.",
    highlight: true,
    lookupKey: "einzelberatung_60min",
  },
  {
    icon: CalendarHeart,
    name: "Begleitpaket",
    duration: "5 Sitzungen à 60 Minuten",
    price: "300 €",
    note: "Für alle, die dranbleiben möchten: fünf Termine zum Vorteilspreis, flexibel planbar.",
    highlight: false,
    lookupKey: "begleitpaket_5",
  },
];

export default function Pricing() {
  const [loading, setLoading] = useState(null);

  const scrollToContact = () => {
    if (window.__lenis) window.__lenis.scrollTo("#kontakt", { offset: -80, duration: 1.2 });
    else document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  const startCheckout = async (lookupKey) => {
    setLoading(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Die Zahlung konnte nicht gestartet werden. Bitte versuch es erneut.");
      setLoading(null);
    }
  };

  return (
    <section
      id="preise"
      data-testid="pricing-section"
      className="relative py-28 sm:py-36 bg-[#EAD0AE]/60"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal className="max-w-2xl mb-16">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#93401D] mb-5">
            Preise
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.02] text-[#29170C]">
            Transparent.
            <br />
            <span className="italic text-[#93401D]">Ohne versteckte Kosten.</span>
          </h2>
          <p className="mt-6 text-[#63432B] text-base sm:text-lg leading-relaxed">
            Du weißt von Anfang an, was dich erwartet. Das Erstgespräch ist
            immer kostenlos – danach entscheidest du in Ruhe, ob und wie es
            weitergeht.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div
                data-testid={`pricing-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative h-full rounded-[1.75rem] p-8 flex flex-col transition-all duration-500 hover:-translate-y-1 ${
                  p.highlight
                    ? "bg-[#93401D] text-[#F2DFC6] shadow-[0_30px_60px_-25px_rgba(181,85,59,0.5)]"
                    : "bg-[#FCF4E8] border border-[#EAD0AE] hover:shadow-[0_18px_36px_-18px_rgba(60,34,19,0.18)]"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    p.highlight ? "bg-[#F2DFC6]/15 text-[#F2DFC6]" : "bg-[#F2DFC6] text-[#93401D]"
                  }`}
                >
                  <p.icon size={22} strokeWidth={1.5} />
                </div>
                <h3
                  className={`font-serif mt-7 text-2xl tracking-tight ${
                    p.highlight ? "text-[#F2DFC6]" : "text-[#29170C]"
                  }`}
                >
                  {p.name}
                </h3>
                <div className={`mt-1 text-sm ${p.highlight ? "text-[#F2DFC6]/70" : "text-[#937353]"}`}>
                  {p.duration}
                </div>
                <div
                  className={`font-serif mt-6 text-5xl tracking-tight ${
                    p.highlight ? "text-[#F2DFC6]" : "text-[#93401D]"
                  }`}
                >
                  {p.price}
                </div>
                <p className={`mt-6 text-[15px] leading-relaxed ${p.highlight ? "text-[#F2DFC6]/85" : "text-[#63432B]"}`}>
                  {p.note}
                </p>
                <div className="mt-auto pt-8">
                  {p.lookupKey ? (
                    <button
                      data-testid={`pay-button-${p.lookupKey}`}
                      onClick={() => startCheckout(p.lookupKey)}
                      disabled={loading === p.lookupKey}
                      className={`rounded-full px-7 py-3 text-sm font-medium inline-flex items-center gap-2 transition-all duration-300 disabled:opacity-70 ${
                        p.highlight
                          ? "bg-[#F2DFC6] text-[#93401D] hover:bg-[#FCF4E8] hover:-translate-y-0.5"
                          : "btn-primary"
                      }`}
                    >
                      {loading === p.lookupKey ? (
                        <><Loader2 size={15} className="animate-spin" /> Wird geladen…</>
                      ) : (
                        <>Jetzt buchen <ArrowRight size={15} strokeWidth={1.8} /></>
                      )}
                    </button>
                  ) : (
                    <button
                      data-testid="pricing-cta-erstgespraech"
                      onClick={scrollToContact}
                      className="btn-ghost rounded-full px-7 py-3 text-sm font-medium inline-flex items-center gap-2"
                    >
                      Termin vereinbaren <ArrowRight size={15} strokeWidth={1.8} />
                    </button>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 flex items-start gap-3 rounded-2xl bg-[#FCF4E8] border border-[#E0BD93] p-6 max-w-3xl">
            <Info size={18} strokeWidth={1.5} className="text-[#93401D] shrink-0 mt-0.5" />
            <p className="text-sm text-[#63432B] leading-relaxed">
              Alle Preise verstehen sich als Endpreise. Die Beratung ist eine
              private Leistung und wird nicht von der Krankenkasse erstattet. Falls
              dir das finanziell schwerfällt, sprich mich gern an – wir finden
              gemeinsam eine Lösung.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
